import React, { useEffect, useState } from 'react';
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Spinner,
  Input,
  InputGroup,
  InputRightElement,
  Icon,
} from '@chakra-ui/react';
import { FiChevronDown, FiSearch } from 'react-icons/fi';
import {
  useCarMakers,
  useModelLines,
  useModifications,
  useYears,
} from 'hooks/use-vehicle-data';
import { useLicensePlateNumberValidator } from 'hooks/use-license-plate-validator';

function RenderField({
  field,
  data,
  label,
  value,
  selectedValues,
  loading,
  isEnabled,
  onOpen,
}) {
  const selectedItem = data.find((item) => item.value === value);
  const displayText = selectedItem ? selectedItem.label : label;
  const isPreFilledByVehicleData =
    (field === 'carMaker' && selectedValues.isCarMakerPreFilled) ||
    (field === 'modelLine' && selectedValues.isModelLinePreFilled) ||
    (field === 'year' && selectedValues.isYearPreFilled) ||
    (field === 'modification' && selectedValues.isModificationPreFilled);
  const canOpen = isEnabled && !isPreFilledByVehicleData && !loading;

  return (
    <Box mb={2}>
      <Button
        variant="outline"
        w="full"
        h="50px"
        justifyContent="space-between"
        onClick={() => canOpen && onOpen(field, data, label)}
        isDisabled={!isEnabled}
        bg={!isEnabled ? 'gray.100' : 'white'}
        color={!isEnabled ? 'gray.400' : 'gray.700'}
        borderColor="gray.300"
        _hover={canOpen ? { bg: 'gray.50' } : {}}
        _disabled={{ bg: 'gray.100', color: 'gray.400', borderColor: 'gray.200' }}
        cursor={isPreFilledByVehicleData ? 'not-allowed' : 'pointer'}
      >
        <Text
          color={
            !value
              ? 'gray.500'
              : isEnabled || isPreFilledByVehicleData
                ? 'gray.700'
                : 'gray.400'
          }
          flex={1}
          fontWeight="normal"
          textAlign="left"
          fontSize="14px"
        >
          {displayText.replace(' ⭐', '')}
        </Text>
        {loading && !selectedItem ? (
          <Spinner size="sm" color="blue.500" />
        ) : (
          <Icon
            as={FiChevronDown}
            color={canOpen ? 'gray.500' : 'gray.300'}
            boxSize={5}
          />
        )}
      </Button>
    </Box>
  );
}

export default function VehicleSearchForm({
  onConfirm,
  selectedValues = {},
  onValueChange,
  onDropdownOpen,
  onError,
  errorMessage,
  showCancelButton = true,
  confirmButtonText = 'Confirm',
  isConfirmButtonDisabled = false,
  onLpnSearch,
  onCancel,
  isLpnSearching = false,
}) {
  const isLicensePlateNumberValid = useLicensePlateNumberValidator();
  const [licensePlateNumber, setLicensePlateNumber] = useState(
    selectedValues.licensePlateNumber || '',
  );
  const [lpnError, setLpnError] = useState('');

  useEffect(() => {
    setLicensePlateNumber(selectedValues.licensePlateNumber || '');
  }, [selectedValues.licensePlateNumber]);

  const { carMakers, loading: loadingMakers } = useCarMakers(onError);
  const { modelLines, loading: loadingModels } = useModelLines(
    selectedValues.carMaker,
    onError,
  );
  const { years, loading: loadingYears } = useYears(
    selectedValues.modelLine,
    selectedValues.carMaker,
    onError,
  );
  const { modifications, loading: loadingMods } = useModifications(
    selectedValues.modelLine,
    selectedValues.year,
    selectedValues.carMaker,
    onError,
  );

  const getDropdownData = (field) => {
    switch (field) {
      case 'carMaker':
        return carMakers;
      case 'modelLine':
        return modelLines;
      case 'year':
        return years;
      case 'modification':
        return modifications;
      default:
        return [];
    }
  };

  const getFieldLabel = (field) => {
    switch (field) {
      case 'carMaker':
        return 'Car Maker';
      case 'modelLine':
        return 'Model Line';
      case 'year':
        return 'Year';
      case 'modification':
        return 'Modification';
      default:
        return '';
    }
  };

  const getSelectedItems = () => {
    const selectedItems = {};

    if (selectedValues.carMaker) {
      selectedItems.carMaker = getDropdownData('carMaker').find(
        (item) => item.value === selectedValues.carMaker,
      );
    }
    if (selectedValues.modelLine) {
      selectedItems.modelLine = getDropdownData('modelLine').find(
        (item) => item.value === selectedValues.modelLine,
      );
    }
    if (selectedValues.year) {
      selectedItems.year = selectedValues.year;
    }
    if (selectedValues.modification) {
      selectedItems.modification = getDropdownData('modification').find(
        (item) => item.value === selectedValues.modification,
      );
    }

    return selectedItems;
  };

  const isFieldEnabled = (field) => {
    switch (field) {
      case 'carMaker':
        return true;
      case 'modelLine':
        return Boolean(selectedValues.carMaker);
      case 'year':
        return Boolean(selectedValues.carMaker && selectedValues.modelLine);
      case 'modification':
        return Boolean(
          selectedValues.carMaker &&
            selectedValues.modelLine &&
            selectedValues.year,
        );
      default:
        return false;
    }
  };

  const handleLpnSearch = () => {
    if (!licensePlateNumber || !onLpnSearch) {
      return;
    }

    const cleanedLicensePlateNumber = licensePlateNumber
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '');

    if (isLicensePlateNumberValid(cleanedLicensePlateNumber)) {
      setLpnError('');
      onValueChange('licensePlateNumber', cleanedLicensePlateNumber);
      onValueChange('isLicensePlateNumberPreFilled', true);
      onLpnSearch(cleanedLicensePlateNumber);
      return;
    }

    setLpnError('Invalid License Plate Number');
  };

  return (
    <Box bg="white" color="gray.800">
      <VStack spacing={4} align="stretch" mb={4}>
        <Box mb={2}>
          <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
            License Plate Number
          </Text>
          <InputGroup>
            <Input
              placeholder="Enter License Plate Number"
              value={licensePlateNumber}
              onChange={(event) =>
                setLicensePlateNumber(event.target.value?.toUpperCase() || '')
              }
              bg="white"
              color="gray.800"
              border="1px solid"
              height="50px"
              borderColor="gray.300"
              _placeholder={{ color: 'gray.400' }}
              _hover={{ borderColor: 'gray.400' }}
              _focus={{
                borderColor: 'blue.500',
                boxShadow: '0 0 0 1px #3182ce',
              }}
              _disabled={{ bg: 'gray.100', color: 'gray.500' }}
              isDisabled={selectedValues.isLicensePlateNumberPreFilled}
              onSubmit={handleLpnSearch}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleLpnSearch();
                }
              }}
            />
            <InputRightElement width="4.5rem" h="50px">
              <Button
                size="sm"
                h="34px"
                onClick={handleLpnSearch}
                isDisabled={
                  !licensePlateNumber ||
                  selectedValues.isLicensePlateNumberPreFilled ||
                  isLpnSearching
                }
                bg="#1F3E76"
                color="white"
                _hover={{ bg: '#183260' }}
                _disabled={{ bg: 'gray.300', cursor: 'not-allowed' }}
                isLoading={isLpnSearching}
              >
                <Icon as={FiSearch} color="#fff" />
              </Button>
            </InputRightElement>
          </InputGroup>
          {lpnError && (
            <Text color="red.500" fontSize="sm" mt={1} mb={1}>
              {lpnError}
            </Text>
          )}
        </Box>

        {['carMaker', 'modelLine', 'year', 'modification'].map((field) => (
          <RenderField
            key={field}
            field={field}
            data={getDropdownData(field)}
            label={getFieldLabel(field)}
            value={selectedValues[field]}
            selectedValues={selectedValues}
            loading={
              (field === 'carMaker' && loadingMakers) ||
              (field === 'modelLine' &&
                selectedValues.carMaker &&
                loadingModels) ||
              (field === 'year' && selectedValues.modelLine && loadingYears) ||
              (field === 'modification' && selectedValues.year && loadingMods)
            }
            isEnabled={isFieldEnabled(field)}
            onOpen={onDropdownOpen}
          />
        ))}
      </VStack>

      {errorMessage && (
        <Text color="red.500" fontSize="sm" mt={1} mb={1}>
          {errorMessage}
        </Text>
      )}

      <HStack spacing={3} mt={1} mb={4}>
        {showCancelButton && (
          <Button
            variant="outline"
            flex={1}
            onClick={onCancel}
            color="gray.600"
            borderColor="gray.300"
            bg="white"
            _hover={{ bg: 'gray.50' }}
          >
            Cancel
          </Button>
        )}
        <Button
          flex={1}
          onClick={() => !isConfirmButtonDisabled && onConfirm(getSelectedItems())}
          isDisabled={isConfirmButtonDisabled}
          bg={isConfirmButtonDisabled ? 'gray.300' : '#1F3E76'}
          color="white"
          _hover={isConfirmButtonDisabled ? {} : { bg: '#183260' }}
          _disabled={{ bg: 'gray.300', color: 'gray.500', cursor: 'not-allowed' }}
        >
          {confirmButtonText}
        </Button>
      </HStack>
    </Box>
  );
}
